// File: src/utils/licenceIssuer.js
// OOOSH Driver Verification — which licensing authority issued this licence
//
// WHY THIS FILE EXISTS
// --------------------
// `licenseIssuedBy === 'DVLA'` was written out by hand in a dozen places across
// this app, and every one of them means "is this a UK driver?". That test is
// WRONG for Northern Ireland: a DVA licence is a UK licence, so OP's router
// (services/driver-validity.ts `isUkLicence`) routes those drivers to the
// licence-record-check step — while this app's copies classified them non-UK
// and sent them to a passport instead. Staff and driver being told different
// things about the same driver is the whole class of bug the OP-side
// consolidation was done to end.
//
// OP IS THE AUTHORITY. These helpers mirror `isUkLicence` / `isNiLicence` in
// the OP repo (backend/src/services/driver-validity.ts). If those change, change
// these — they must agree about the same driver or the driver's own journey
// disagrees with the staff screen.
//
// NOT CONVERTED (deliberately): the Monday.com legacy paths —
// `functions/get-next-step.js`, `functions/monday-integration.js` — keep their
// own inline copies. Monday is dead (DATA_BACKEND=op since Apr 2026) and those
// branches no longer run; rewriting them would be churn on untested code.

/** The issuer string iDenfy returns for a Northern Ireland licence. */
const DVA_ISSUER = 'DVA';

/**
 * Is this a UK licence — i.e. one that needs a licence record check rather
 * than a passport?
 *
 * TRUE for Northern Ireland. NI is the UK; see isNiIssuer for what NI drivers
 * can and cannot do about it.
 */
export function isUkIssuer(licenseIssuedBy) {
  const issuedBy = String(licenseIssuedBy || '').trim().toUpperCase();
  return issuedBy.includes('DVLA') || issuedBy === DVA_ISSUER;
}

/**
 * Is this a Northern Ireland (DVA) licence?
 *
 * These drivers need the same record check as any other UK driver, but
 * `viewdrivingrecord.service.gov.uk` holds GB licences only — so they CANNOT
 * produce one. DVA run their own service at nidirect where the driver creates a
 * single-use check code and a third party performs the lookup.
 *
 * Which is why this is a branch in the DVLA step rather than a new router step:
 * `dvla-check` is already the right destination, and OP sends a DVA driver
 * there correctly. It was the page's hardcoded gov.uk link that dead-ended
 * them — six router branches all leading back to a service that would never
 * hold their licence (Declan Haughian / 16286, Sep 2026).
 */
export function isNiIssuer(licenseIssuedBy) {
  return String(licenseIssuedBy || '').trim().toUpperCase() === DVA_ISSUER;
}
